import { createReducer } from '__common/utils/helpers';

import { insertPanelIntoRtree } from '__editor/panelsEditor/components/panels/panelsCollisions';
import { removePanelFromRtree } from '__editor/panelsEditor/components/panels/panelsCollisions';
import { resetRTree } from '__editor/panelsEditor/components/panels/panelsCollisions';
import { DrawingManagerActionTypes, PanelsActionTypes } from 'actionsConstants';
import {
  getPanelCollisionBounds,
} from '__editor/panelsEditor/components/panels/panels';
import { checkObstructionZoneForPanelsInPanelEditor } from '../obstructions/obstructionsHelper';
import { setExposureForSibligns } from './utils/panelsManagment';
import { checkPanelsExposure } from './utils/exposureRecheck';
import hash from 'object-hash';
import { Action } from '__common/store/action';
import { ADD_PANELS_ACTION, LOAD_PANELS_ACTION, UPDATE_PANELS_ACTION, RECHECK_PANELS_EXPO_ACTION, RECHECK_PANELS_OBSTRUCTION_ZONE_ACTION, CHANGE_DESIRED_TABLE_LENGTH_ACTION, CHANGE_DESIRED_TABLE_WIDTH_ACTION, ADD_HASHMAP_ACTION, REMOVE_PANELS_ACTION } from './panelsActions';
import _ from 'lodash';
import store from '__common/store';
import { isRMDT, isSingleColumnRestrictedProduct } from '__common/constants/products';
import { greaterThanEqualToProjectVersion } from '__common/utils/versionCompare/versionCompare';
import { VERSION_MAP } from '__common/utils/versionCompare/version_info';
import { applySingleColumnPendingRemovalState, SingleColumnSpacingPx } from './utils/singleColumnPendingRemoval';
import { getPanelUpdatePatch } from './utils/getPanelUpdatePatch';

function applyManualExposureToPanel(panel: panelInState, kind: 'E' | 'NE'): panelInState {
  if (panel.manualExposure === kind) {
    const cleared = { ...panel };
    if (cleared.manualExposureOrigExposed !== undefined) {
      cleared.exposed = !!cleared.manualExposureOrigExposed;
    }
    if (cleared.manualExposureOrigExposedNeighbour !== undefined) {
      cleared.exposedNeighbour = !!cleared.manualExposureOrigExposedNeighbour;
    }
    delete cleared.manualExposure;
    delete cleared.manualExposureOrigExposed;
    delete cleared.manualExposureOrigExposedNeighbour;
    return cleared;
  }
  const origExposed = panel.manualExposureOrigExposed !== undefined
    ? panel.manualExposureOrigExposed
    : panel.exposed;
  const origExposedNeighbour = panel.manualExposureOrigExposedNeighbour !== undefined
    ? panel.manualExposureOrigExposedNeighbour
    : panel.exposedNeighbour;
  return {
    ...panel,
    manualExposure: kind,
    exposed: kind === 'E',
    exposedNeighbour: kind === 'E' ? !!panel.exposedNeighbour : false,
    manualExposureOrigExposed: origExposed,
    manualExposureOrigExposedNeighbour: origExposedNeighbour,
  };
}

const shouldApplySingleColumnPendingRemoval = () => {
  const { projectConfiguration: { productId, projectVersion } } = store.getState();

  return isSingleColumnRestrictedProduct(productId) &&
    greaterThanEqualToProjectVersion(projectVersion, VERSION_MAP['single_column_restriction']);
};

const getSingleColumnSpacingPx = (spacingPx?: SingleColumnSpacingPx): SingleColumnSpacingPx | undefined => {
  if (spacingPx) {
    return spacingPx;
  }

  const { settings: { rowSpacing, columnSpacing }, background: { metersPerPixel } } = store.getState();

  if (!metersPerPixel && metersPerPixel !== 0) {
    return undefined;
  }

  return {
    rowSpacingPx: rowSpacing / metersPerPixel,
    columnSpacingPx: columnSpacing / metersPerPixel,
  };
};

const applySingleColumnPendingRemovalIfNeeded = (
  panels: panelInState[],
  spacingPx?: SingleColumnSpacingPx,
) => {
  if (!shouldApplySingleColumnPendingRemoval()) {
    return panels;
  }

  return applySingleColumnPendingRemovalState(
    panels,
    getSingleColumnSpacingPx(spacingPx),
  );
};

// RMDT modules share a siblingId. After the requested ids are removed, a
// sibling left on its own is removed too. This stays inside the panels slice
// so the reducer does not call store.getState(), which Redux 4 rejects.
function dropUnpairedSiblingPanels(panels: panelInState[], ids: number[]): panelInState[] {
  const removed = new Set(ids);
  const remaining = panels.filter(panel => !removed.has(panel.id));
  const counts = new Map<number | undefined, number>();
  remaining.forEach((panel) => {
    counts.set(panel.siblingId, (counts.get(panel.siblingId) || 0) + 1);
  });
  return remaining.filter(panel => (counts.get(panel.siblingId) || 0) > 1);
}

export const panelsState: panelsState = {
  panels: [],
  centerOffset: { x: 0, y: 0 },
  creatingBlocked: false,
  enabledRoofZones: true,
  exposureRecalculation: false,
  exposureRecaluclationHash: null,
  desiredTableLength: null,
  desiredTableWidth: null,
  desiredTableSizeByRoofId: {},
  panelsToBeMoved: [],
  panelsArrangementToBeMoved: { panelsToBeMovedGrid: [] , panelsToggleAttachmentGrid: [], panelConfig: null },
  bays: {
    panelBayMapping: new Map(),
    visitedBays:[],
  },
};

export default {
  panels: createReducer(panelsState, {
    // Adds new panels to the state
    [PanelsActionTypes.ADD_PANELS](state: panelsState, action: Action<ADD_PANELS_ACTION>): panelsState {
      return {
        ...state,
        panels: (action.payload as any)?.allPanels || action.payload.panels,
      };
    },
    // Updates the mapping between panels and bays
    [PanelsActionTypes.ADD_PANEL_BAY_MAPPING](state: panelsState, action: Action<ADD_HASHMAP_ACTION>): panelsState {
      return {
        ...state,
        bays: {
          ...state.bays,
          panelBayMapping: action.payload.panelBayMapping,
        },
      };
    },
    // Starts the process of recalculating panel exposure
    [PanelsActionTypes.START_EXPOSURE_RECALCULATION](state): panelsState {
      return { ...state, exposureRecalculation: true };
    },
    [PanelsActionTypes.FINISH_EXPOSURE_RECALCULATION](state): panelsState {
      return { ...state, exposureRecalculation: false };
    },
    // Rechecks exposure for specific panels and updates siblings
    [PanelsActionTypes.RECHECK_PANELS_EXPO](state: panelsState, action: Action<RECHECK_PANELS_EXPO_ACTION>): panelsState {
      const {
        panelsToRecheck,
        productId,
        roofId,
        stateConfig,
      } = action.payload;
      let panels = checkPanelsExposure(state.panels, panelsToRecheck, roofId, stateConfig);
      panels = setExposureForSibligns(panels, productId); 
      const exposureRecaluclationHash = hash(panels);
      return { ...state, panels, exposureRecalculation: false, exposureRecaluclationHash };
    },
    [PanelsActionTypes.RECHECK_PANELS_OBSTRUCTION_ZONE](state: panelsState, action: Action<RECHECK_PANELS_OBSTRUCTION_ZONE_ACTION>) {
      const { selectedRoofId, metersPerPixel, bgX, bgY, productId, obstructions, projectConfiguration, stateConfig } = action.payload;
      const panels = checkObstructionZoneForPanelsInPanelEditor(state.panels, selectedRoofId, metersPerPixel, bgX, bgY, productId, obstructions, projectConfiguration, stateConfig);
      return { ...state, panels };
    },
    [PanelsActionTypes.REMOVE_PANELS](state: panelsState, action: Action<REMOVE_PANELS_ACTION>): panelsState {
      const { panelsIds, productId } = action.payload;
      let { panelBayMapping, visitedBays } = state.bays;

      if (!Array.isArray(visitedBays)) {
        visitedBays = [];
      }

      const panels = productId != null && isRMDT(productId)
        ? dropUnpairedSiblingPanels(state.panels, panelsIds)
        : state.panels.filter(panel => !panelsIds.includes(panel.id));
      const keptIds = new Set(panels.map(panel => panel.id));
      const removedIds: number[] = [];

      state.panels.forEach((panel) => {
        if (keptIds.has(panel.id)) {
          return;
        }
        removedIds.push(panel.id);
        if (panel.rTreeBounds) {
          removePanelFromRtree(panel.rTreeBounds);
        }
      });

      if (panelBayMapping instanceof Map && panelBayMapping.size) {
        panelBayMapping = new Map(panelBayMapping);
        const deletedBaysSet = new Set<number>();
        removedIds.forEach((id: number) => {
          const bays = panelBayMapping.get(id);
          if (bays) {
            bays.forEach(bay => deletedBaysSet.add(bay.id));
          }
          panelBayMapping.delete(id);
        });
        visitedBays = visitedBays.filter(id => !deletedBaysSet.has(id));
      }

      return { 
        ...state, 
        panels, 
        exposureRecaluclationHash: null,
        bays: {
          ...state.bays,
          panelBayMapping,
          visitedBays,
        },
      };
    },
    [PanelsActionTypes.BLOCK_CREATING_PANEL](state: panelsState) {
      return { ...state, creatingBlocked: true };
    },
    [PanelsActionTypes.UNBLOCK_CREATING_PANEL](state: panelsState) {
      return { ...state, creatingBlocked: false };
    },
    // Resets the entire panels state to initial values
    [PanelsActionTypes.RESET_PANELS_STATE](state: panelsState) {
      return { ...state, panels: [], creatingBlocked: false, exposureRecaluclationHash: null, panelsToBeMoved: [], panelsArrangementToBeMoved: { panelsToBeMovedGrid:[], panelsToggleAttachmentGrid: [], panelConfig : null }, bays:{ panelBayMapping: new Map(), visitedBays: [] } };
    },
    // Loads panels from a previous state (undo/redo or history)
    [PanelsActionTypes.LOAD_PANELS_FROM_HISTORY](state: panelsState, action: action & { payload: { panels: panelInState[], rowSpacing: number, columnSpacing: number, metersPerPixel: number } }): panelsState {
      const {
        rowSpacing,
        columnSpacing,
        metersPerPixel,
      } = action.payload;

      resetRTree();

      const panels = (action.payload.panels || []).map((panel) => {
        const rTreeBounds = getPanelCollisionBounds(panel, rowSpacing, columnSpacing, metersPerPixel);
        insertPanelIntoRtree(rTreeBounds);

        return {
          ...panel,
          rTreeBounds,
        };
      });

      return {
        ...state,
        panels,
        exposureRecaluclationHash: null,
      };
    },
    // Standard panel loading with collision initialization.
    // Snap/cursor trees are hydrated from the rendered PIXI panels so they can be
    // cleaned up from the same object graph later.
    [PanelsActionTypes.LOAD_PANELS](state: panelsState, action: Action<LOAD_PANELS_ACTION>): panelsState {
      const { rowSpacing, columnSpacing, metersPerPixel, exposureRecaluclationHash } = action.payload;
      const panelsToLoad = action.payload.panels;

      resetRTree();

      const panels = panelsToLoad.map((panel) => {
        const updatedPanel = { ...panel };

        if (action.payload.shouldCenter) {
          updatedPanel.x += state.centerOffset.x;
          updatedPanel.y += state.centerOffset.y;
        }

        const bounds = getPanelCollisionBounds(updatedPanel, rowSpacing, columnSpacing, metersPerPixel);

        updatedPanel.rTreeBounds = bounds;
        insertPanelIntoRtree(bounds);

        return updatedPanel;
      });

      return {
        ...state,
        panels,
        centerOffset: { x: 0, y: 0 },
        exposureRecaluclationHash,
      };
    },
    // Re-calculates and inserts collision bounds for all current panels
    [PanelsActionTypes.RESTORE_PANELS_COLLISIONS_BOUNDS](state: panelsState, action) {
      const { rowSpacing, columnSpacing, metersPerPixel } = action.payload;

      const panels = state.panels.map((panel) => {
        const rTreeBounds = getPanelCollisionBounds(panel, rowSpacing, columnSpacing, metersPerPixel);
        insertPanelIntoRtree(rTreeBounds);
        return { ...panel, rTreeBounds };
      });

      return { ...state, panels };
    },
    // Sets whether roof zone autofill is enabled
    [PanelsActionTypes.SET_ROOF_ZONES_AUTOFILL](state: panelsState, action: { payload: { status: boolean } }) {
      return { ...state, enabledRoofZones: action.payload.status };
    },
    // Changes the roof zone of a specific panel
    [PanelsActionTypes.CHANGE_SINGLE_PANEL_ROOF_ZONE](state: panelsState, action: { payload: { panelId: number, newRoofZone: roofZoneNumber } }) {
      const { panels } = state;
      const { panelId, newRoofZone } = action.payload;

      const updatedPanels = panels.map((panel) => {
        if (panel.id === panelId) {
          return { ...panel, roofZone: newRoofZone };
        }
        return panel;
      });

      return { ...state, panels: updatedPanels };
    },
    // Sets the global center offset for panels
    [PanelsActionTypes.SET_CENTER_OFFSET](state: panelsState, action: { payload: { offset: { x: number, y: number } } }) {
      const { offset } = action.payload;

      return { ...state, centerOffset: offset };
    },
    // Updates existing panels with new data, optionally targeting specific properties
    [PanelsActionTypes.UPDATE_PANELS](state: panelsState, action: Action<UPDATE_PANELS_ACTION>): panelsState {
      const { payload: { panels, type } } = action;
      const panelsMap = new Map(panels.map(p => [p.id, p]));
      const updatedPanels = state.panels.map((oldPanel) => {
        const foundPanel = panelsMap.get(oldPanel.id);
        if (foundPanel) {
          return {
            ...oldPanel,
            ...getPanelUpdatePatch(foundPanel, type?.type),
          };
        }
        return oldPanel;
      });
      const updatedPanelsToBeMoved = state.panelsToBeMoved.map((oldPanel) => {
        const foundPanel = panelsMap.get(oldPanel.id);
        if (foundPanel) {
          return {
            ...oldPanel,
            ...getPanelUpdatePatch(foundPanel, type?.type),
          };
        }
        return oldPanel;
      });
      return { ...state, panels: updatedPanels, panelsToBeMoved: updatedPanelsToBeMoved };
    },
    // Sets desired table dimensions for GFT/ULA
    [PanelsActionTypes.CHANGE_DESIRED_TABLE_LENGTH](state: panelsState, action: Action<CHANGE_DESIRED_TABLE_LENGTH_ACTION>): panelsState {
      return { ...state, desiredTableLength: action.payload.length };
    },
    [PanelsActionTypes.CHANGE_DESIRED_TABLE_WIDTH](state: panelsState, action: Action<CHANGE_DESIRED_TABLE_WIDTH_ACTION>): panelsState {
      return { ...state, desiredTableWidth: action.payload.width };
    },
    [PanelsActionTypes.RESET_TABLE_LENGTH_AND_WIDTH_SIZE](state: panelsState): panelsState {
      return { ...state, desiredTableLength: null, desiredTableWidth: null, desiredTableSizeByRoofId: {} };
    },
    [DrawingManagerActionTypes.SET_ROOF_DESIRED_TABLE_SIZE](state: panelsState, action): panelsState {
      const { roofId, desiredTableLength, desiredTableWidth } = action.payload;
      const existing = state.desiredTableSizeByRoofId?.[roofId] || {};
      return {
        ...state,
        desiredTableSizeByRoofId: {
          ...state.desiredTableSizeByRoofId,
          [roofId]: {
            ...existing,
            ...(desiredTableLength !== undefined ? { length: desiredTableLength } : {}),
            ...(desiredTableWidth !== undefined ? { width: desiredTableWidth } : {}),
          },
        },
      };
    },
    [DrawingManagerActionTypes.DELETE_ROOF](state: panelsState, action: { payload: { id: number } }): panelsState {
      const { id } = action.payload;
      const desiredTableSizeByRoofId = { ...state.desiredTableSizeByRoofId };
      delete desiredTableSizeByRoofId[id];
      delete desiredTableSizeByRoofId[String(id)];
      return { ...state, desiredTableSizeByRoofId };
    },
    // Manages panel selection for movement operations
    [PanelsActionTypes.SET_PANELS_ARRAY_FOR_MOVEMENT](state: panelsState, action): panelsState {
      return { ...state, panelsToBeMoved: action.payload.panels };
    },
    [PanelsActionTypes.SET_PANELS_ARRAY_GRID_FOR_MOVEMENT](state: panelsState, action): panelsState {
      const { grid, toggleAttachmentsGrid, panelConfig } = action.payload;
      return { ...state, panelsArrangementToBeMoved:  { panelsToBeMovedGrid: grid, panelsToggleAttachmentGrid: toggleAttachmentsGrid, panelConfig } };
    },
    [PanelsActionTypes.RESET_PANELS_ARRAY_FOR_MOVEMENT](state: panelsState, action): panelsState {
      return { ...state, panelsToBeMoved:  [] , panelsArrangementToBeMoved:{ panelsToBeMovedGrid: [], panelsToggleAttachmentGrid: [], panelConfig: null } };
    },
    // Toggles the overall attachment state of a panel
    [PanelsActionTypes.TOGGLE_PANEL_ATTACHED](state: panelsState, action): panelsState {
      const panelId = action.payload.panelId;
      const panels = state.panels.map(panel => {
        if (panel.id === panelId) {
          return { ...panel, attached: !panel.attached };
        }
        return panel; 
      }); 
      return { ...state, panels };
    },
    [PanelsActionTypes.SET_MANUAL_EXPOSURE](state: panelsState, action): panelsState {
      const { panelId, kind } = action.payload;
      const panels = state.panels.map(panel => {
        if (panel.id !== panelId) return panel;
        return applyManualExposureToPanel(panel, kind);
      });
      return { ...state, panels };
    },
    [PanelsActionTypes.SET_MANUAL_EXPOSURE_BULK](state: panelsState, action): panelsState {
      const { panelIds, kind } = action.payload;
      const idSet = new Set<number>(panelIds);
      const panels = state.panels.map(panel => {
        if (!idSet.has(panel.id)) return panel;
        return applyManualExposureToPanel(panel, kind);
      });
      return { ...state, panels };
    },
    [PanelsActionTypes.CLEAR_ALL_MANUAL_EXPOSURE](state: panelsState): panelsState {
      const panels = state.panels.map(panel => {
        if (
          panel.manualExposure === undefined &&
          panel.manualExposureOrigExposed === undefined &&
          panel.manualExposureOrigExposedNeighbour === undefined
        ) {
          return panel;
        }
        const cleared = { ...panel };
        if (cleared.manualExposureOrigExposed !== undefined) {
          cleared.exposed = !!cleared.manualExposureOrigExposed;
        }
        if (cleared.manualExposureOrigExposedNeighbour !== undefined) {
          cleared.exposedNeighbour = !!cleared.manualExposureOrigExposedNeighbour;
        }
        delete cleared.manualExposure;
        delete cleared.manualExposureOrigExposed;
        delete cleared.manualExposureOrigExposedNeighbour;
        return cleared;
      });
      return { ...state, panels };
    },
    // Toggles attachment for a specific bay within a panel
    [PanelsActionTypes.TOGGLE_BAY_ATTACHED](state: panelsState, action): panelsState {
      const bayId = action.payload.bayId;
      const panelId = action.payload.panelId;
      const mapping = new Map([...Array.from(state.bays.panelBayMapping)]);
      mapping.get(panelId)?.map(bay=> {
        if(bay.id === bayId){
          bay.attached = bay.attached ? 0 : 1;
        }
      });
      return { ...state, bays: { ...state.bays, panelBayMapping: mapping } };
    },
    // Updates the list of visited bays
    [PanelsActionTypes.ADD_VISITED_BAYS](state: panelsState, action): panelsState {
      const bays = action.payload.bays;
      return { ...state, bays:{ ...state.bays, visitedBays:bays } };
    },
  }),
};
